import React, { memo } from 'react';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Autocomplete, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import SelectDatePicker from '../../../components/audit/SelectDatePicker';
import type { EmailPipelineFiltersBarProps, EmailPipelineStoreOption } from '../../../types/features/emailPipeline';
import { EMAIL_PIPELINE_STATUS_OPTIONS, getStoreLabel } from '../mappers/emailPipelineFormatters';
import { adminBorders, adminColors } from '../../../styles/adminDesignTokens';
import {
  adminAutocompleteSx,
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminFormControlSx,
} from '../../../styles/adminComponentStyles';

const EmailPipelineFiltersBar: React.FC<EmailPipelineFiltersBarProps> = ({
  model,
  actions,
}) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      borderBottom: adminBorders.divider,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1.5,
      flexWrap: 'wrap',
      backgroundColor: adminColors.surface,
    }}
  >
    <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ flexShrink: 0 }}
      >
        {model.showStoreFilter ? (
          <Autocomplete
          size="small"
          options={model.stores}
          loading={model.storesLoading}
          value={model.selectedStore}
          inputValue={model.storeSearchInput}
          onInputChange={(_, value) => actions.onStoreSearchInputChange(value)}
          onChange={(_, value) => actions.onStoreChange(value?.id || '')}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option: EmailPipelineStoreOption) => getStoreLabel(option)}
          filterOptions={(options, state) => {
            const query = state.inputValue.trim().toLowerCase();
            if (!query) return options;
            return options.filter((option) => {
              const name = (option.name || '').toLowerCase();
              const storeKey = (option.store_key || '').toLowerCase();
              return name.includes(query) || storeKey.includes(query);
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search Store "
              error={Boolean(model.storesError)}
              helperText={model.storesError || ''}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchOutlinedIcon sx={{ mr: 0.8, color: adminColors.subtleText }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={adminAutocompleteSx}
            />
          )}
          sx={adminCombineSx(adminAutocompleteSx, {
            minWidth: { xs: '100%', sm: 280 },
          })}
          />
        ) : null}
        <Button
          variant="contained"
          onClick={actions.onBulkProcess}
          disabled={model.bulkActionLoading || model.selectedIdsCount === 0}
          sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, {
            height: 36,
            minWidth: 180,
            display: model.selectedIdsCount > 0 ? 'inline-flex' : 'none',
          })}
        >
          {model.bulkActionLoading ? 'Processing...' : 'Reprocess'}
        </Button>
      </Stack>

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
      <FormControl
        size="small"
        sx={adminCombineSx(adminFormControlSx, { minWidth: { xs: '100%', sm: 144 } })}
      >
        <InputLabel shrink>Status</InputLabel>
        <Select
          label="Status"
          value={model.statusFilter}
          onChange={(event) => actions.onStatusFilterChange(event.target.value)}
          IconComponent={KeyboardArrowDownRoundedIcon}
          displayEmpty
        >
          {EMAIL_PIPELINE_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.label} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ minWidth: { xs: '100%', sm: 218 }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
        <SelectDatePicker value={model.dateRangeFilter} onChange={actions.onDateRangeChange} />
      </Box>

    </Box>
  </Box>
);

export default memo(EmailPipelineFiltersBar);
